/**
 * Iter 65 — Google Drive integration setup page.
 *
 * Lets the super-admin connect their own Google Drive (own OAuth client),
 * upload large tender PDFs from anywhere, list, search, and delete files.
 * Files live in their Drive — zero server bandwidth or disk used.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/format';
import { ArrowLeft, HardDrive, Loader2, RefreshCw, Upload, ExternalLink, Trash2, Search, Cloud, CheckCircle2, XCircle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useConfirm } from '../../components/ConfirmDialog';
import { apiGet, apiPost, apiDel, API } from '../../lib/api';

function fmtSize(bytes) {
  if (!bytes) return '—';
  const b = parseInt(bytes, 10);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function GoogleDriveSettings() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [confirm, ConfirmHost] = useConfirm();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await apiGet('/drive/status');
      setStatus(s);
      if (s.connected) {
        const f = await apiGet('/drive/files');
        setFiles(f.files || []);
      }
    } catch (e) {
      toast.error('Failed to load Drive status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get('connected') === 'true') {
      toast.success('Google Drive connected successfully');
      load();
    } else if (params.get('error')) {
      toast.error(`Drive connection failed: ${params.get('error')}`);
    }
  }, [params, load]);

  const connect = async () => {
    try {
      // pass the current frontend origin so the backend builds a
      // redirect URI on the SAME environment (preview vs production).
      const r = await apiGet(`/drive/connect?origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = r.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start OAuth');
    }
  };

  const disconnect = async () => {
    const ok = await confirm({
      title: 'Disconnect Google Drive?',
      body: 'Your stored OAuth token will be wiped from this server. Files already uploaded to Drive stay in your Drive — only the connection is removed.\n\nYou can reconnect anytime.',
      confirmLabel: 'Disconnect',
      danger: true,
    });
    if (!ok) return;
    try {
      await apiDel('/drive/disconnect');
      toast.success('Disconnected');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setProgress(0);
    const form = new FormData();
    form.append('file', file);
    form.append('folder_name', 'Ellora EPC');
    try {
      const token = localStorage.getItem('access_token');
      // XHR for progress UI (axios + percent is fiddlier)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/drive/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(xhr.responseText);
        xhr.onerror = () => reject('Network error');
        xhr.send(form);
      });
      toast.success(`Uploaded "${file.name}" → Drive`);
      load();
    } catch (err) {
      toast.error(`Upload failed: ${err}`);
    } finally { setUploading(false); setProgress(0); e.target.value = ''; }
  };

  const onDelete = async (f) => {
    const ok = await confirm({
      title: 'Delete file from Drive?',
      body: `"${f.name}" will be moved to your Google Drive trash.\n\nThis cannot be undone via this app (you'd need to restore from Drive directly).`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try { await apiDel(`/drive/files/${f.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 size={20} className="animate-spin inline mr-2" />Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="drive-page">
      {ConfirmHost}
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Cloud Storage" title="Google Drive Integration" subtitle="Store huge tender / project documents (1-2 GB PDFs) in your Drive — zero server bandwidth used." icon={HardDrive} />

      {/* Status / setup card */}
      {!status?.configured ? (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-5 mt-6" data-testid="not-configured-banner">
          <div className="font-display font-bold uppercase tracking-wider text-amber-900 text-xs mb-2">⚠ Setup Required</div>
          <p className="text-sm text-amber-900 mb-3">
            Your Google Drive OAuth client is not yet configured on the server. Follow these one-time steps:
          </p>
          <ol className="text-sm text-amber-900 list-decimal list-inside space-y-1 mb-3">
            <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="underline">Google Cloud Console</a> → create a project.</li>
            <li>Enable the <strong>Google Drive API</strong>.</li>
            <li>OAuth consent screen → <em>External</em> → add scopes <code className="bg-amber-100 px-1">drive.file</code> + <code className="bg-amber-100 px-1">userinfo.email</code> → add yourself as a Test User.</li>
            <li>Credentials → Create OAuth Client ID → <strong>Web application</strong>.<br />
              <span className="text-xs">Authorised redirect URI: <code className="bg-amber-100 px-1">{window.location.origin.replace('//ellora-erp-mvp-1.preview', '//YOUR-BACKEND.preview')}/api/drive/callback</code></span>
            </li>
            <li>Copy the Client ID + Secret. Add to backend <code className="bg-amber-100 px-1">.env</code>:
              <pre className="bg-white border border-amber-200 rounded-sm p-2 mt-1 text-xs font-mono overflow-x-auto">{`GOOGLE_DRIVE_CLIENT_ID="your-client-id"
GOOGLE_DRIVE_CLIENT_SECRET="your-client-secret"
GOOGLE_DRIVE_REDIRECT_URI="${window.location.origin}/api/drive/callback"
FRONTEND_URL="${window.location.origin}"`}</pre>
            </li>
            <li>Ask the developer to restart the backend, then refresh this page.</li>
          </ol>
          <p className="text-xs text-amber-800">{status?.message}</p>
        </div>
      ) : !status?.connected ? (
        <div className="bg-white border border-slate-200 rounded-sm p-6 mt-6 text-center" data-testid="not-connected-banner">
          <Cloud size={36} className="mx-auto text-slate-300 mb-2" />
          <h3 className="font-display font-bold text-lg">Connect your Google Drive</h3>
          <p className="text-sm text-slate-600 mt-1 mb-4">OAuth client is configured. Click below to authorize access to your Drive.</p>
          <button onClick={connect} className="bg-[#3b4872] text-white px-6 py-2.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm hover:bg-[#2d3856]" data-testid="connect-btn">
            Connect Google Drive
          </button>
        </div>
      ) : (
        <>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="connected-banner">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-600" />
              <div>
                <div className="font-display font-bold text-sm text-emerald-900">Drive Connected</div>
                <div className="text-xs text-emerald-900 mt-0.5" data-testid="drive-google-email">
                  <strong>Google account:</strong> {status.google_email || status.user_email}
                  {status.google_name && <span className="text-emerald-800/80"> · {status.google_name}</span>}
                </div>
                {status.google_email && status.google_email.toLowerCase() !== (status.user_email || '').toLowerCase() && (
                  <div className="text-[10px] text-emerald-800/80 mt-0.5">
                    Connected by ERP user <span className="font-mono">{status.user_email}</span>
                  </div>
                )}
                <div className="text-[10px] text-emerald-800/70 font-mono mt-1">scopes: {(status.scopes || []).filter((s) => s.includes('drive')).map((s) => s.split('/').pop()).join(', ')}</div>
              </div>
            </div>
            <button onClick={disconnect} className="px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50 flex items-center gap-1.5" data-testid="disconnect-btn">
              <XCircle size={11} /> Disconnect
            </button>
          </div>

          {/* Upload row */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 mt-4 flex items-center justify-between flex-wrap gap-3" data-testid="upload-card">
            <div className="flex-1">
              <h3 className="font-display font-bold text-sm">Upload to "Ellora EPC" folder on your Drive</h3>
              <p className="text-xs text-slate-500 mt-0.5">Multi-GB files are streamed in 4 MB chunks (resumable). Nothing is kept on this server.</p>
              {uploading && (
                <div className="mt-2">
                  <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-[#3b4872] transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">Uploading… {progress}%</div>
                </div>
              )}
            </div>
            <label className={`px-4 py-2 text-xs font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm cursor-pointer hover:bg-[#2d3856] flex items-center gap-1.5 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="upload-btn">
              <Upload size={11} /> {uploading ? 'Uploading…' : 'Choose File'}
              <input type="file" hidden onChange={onUpload} disabled={uploading} data-testid="upload-input" />
            </label>
          </div>

          {/* File list */}
          <div className="bg-white border border-slate-200 rounded-sm mt-4 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <h3 className="font-display font-bold text-sm">Files in Drive · "Ellora EPC" folder</h3>
              <div className="flex items-center gap-2">
                <Search size={12} className="text-slate-400" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="border border-slate-300 rounded-sm px-2.5 py-1 text-xs w-48" data-testid="search-input" />
                <button onClick={load} className="text-xs px-2.5 py-1 border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-indigo-50 flex items-center gap-1" data-testid="refresh-btn"><RefreshCw size={10} /> Refresh</button>
              </div>
            </div>
            {files.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-500">No files yet. Upload one above.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
                  <tr>
                    <th className="text-left py-2.5 px-3">Name</th>
                    <th className="text-left py-2.5 px-3 w-24">Size</th>
                    <th className="text-left py-2.5 px-3 w-44">Modified</th>
                    <th className="text-left py-2.5 px-3 w-40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.filter((f) => !query || f.name.toLowerCase().includes(query.toLowerCase())).map((f) => (
                    <tr key={f.id} className="border-t border-slate-100" data-testid={`drive-row-${f.id}`}>
                      <td className="py-2 px-3"><span className="font-mono text-xs">{f.name}</span></td>
                      <td className="py-2 px-3 text-xs tabular-nums">{fmtSize(f.size)}</td>
                      <td className="py-2 px-3 text-xs text-slate-600 whitespace-nowrap">{f.modifiedTime ? formatDateTime(f.modifiedTime) : '—'}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <a href={f.webViewLink} target="_blank" rel="noreferrer" className="px-2.5 py-1 text-[10px] font-display font-bold uppercase bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] flex items-center gap-1" data-testid={`open-${f.id}`}>
                            <ExternalLink size={10} /> Open
                          </a>
                          <button onClick={() => onDelete(f)} className="text-rose-500 hover:text-rose-700" title="Delete from Drive" data-testid={`drive-del-${f.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
